import {
    Component,
    DestroyRef,
    EventEmitter,
    Output,
    ChangeDetectionStrategy,
    signal,
    WritableSignal,
    OnDestroy,
    OnInit,
    effect,
    computed,
    inject,
} from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { Subscription } from 'rxjs'
import { IMAGE_PATHS } from '../../core/constants/assets.constants'
import { AuthService } from '@core/services/auth-service/auth.service'
import { IUser, UserPermissions } from '@app/core/models/user.model'
import { environment } from 'src/environments/environment'
import { authEndpoints } from '@app/core/constants/valueConstants'
import { PermissionService } from '@core/services/permission-service/permission-service'
import { UserStore } from '@core/services/user/user.store'
import { Router, NavigationEnd, RouterLink } from '@angular/router'
import { catchError, filter, map, startWith, switchMap } from 'rxjs/operators'
import { AppConstants } from '@app/core/constants/app.constants'
import { determineUserRoleFromPermissions } from '@app/features/crm/helpers/role.helper'
import { EUserType } from '@app/core/enum/client.enum'
import { IDashboardService } from '@core/services/dashboard/dashboard.service'
import { SvgIconComponent } from '../../shared/components/svg-icon/svg-icon.component'
import { ZardDropdownMenuComponent } from '../../../styles/components/dropdown/dropdown.component'
import { ZardDividerComponent } from '../../../styles/components/divider/divider.component'
import { ZardDropdownMenuItemComponent } from '../../../styles/components/dropdown/dropdown-item.component'
import { UserMenuComponent } from '../../shared/components/user-menu/user-menu.component'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { EiDropdownComponent } from '@app/shared/components/ei-dropdown/ei-dropdown.component'
import { ShikshaDashboardFiltersService } from '@app/core/services/shiksha/shiksha-dashboard-filters.service'
import { SHIKSHA_DROPDOWN_CONTENT } from '@app/features/home/shiksha-components/constants/shiksha-dashboard.constants'
import { ReporteesService } from '@core/services/reportees/reportees.service'
import { combineLatest, of } from 'rxjs'

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SvgIconComponent,
        RouterLink,
        ZardDropdownMenuComponent,
        ZardDividerComponent,
        ZardDropdownMenuItemComponent,
        UserMenuComponent,
        ReactiveFormsModule,
        EiDropdownComponent,
    ],
})
export class HeaderComponent implements OnDestroy, OnInit {
    authService = inject(AuthService)
    private permissionService = inject(PermissionService)
    private userStore = inject(UserStore)
    private router = inject(Router)

    @Output() toggleSidebar = new EventEmitter<void>()

    readonly navigatorLogoPath = IMAGE_PATHS.NAVIGATOR_LOGO
    readonly avatarPath = IMAGE_PATHS.AVATAR
    readonly notificationCount: WritableSignal<number> = signal(0)
    readonly salesCycleLabel = AppConstants.SALES_CYCLE_LABEL
    readonly EUserType = EUserType

    public userInfo: IUser | null = null
    public isAuthenticated: WritableSignal<boolean> = signal(false)
    public loggedInUserRole: WritableSignal<EUserType | null> = signal(null)
    readonly isHomeRoute = signal<boolean>(false)
    readonly isShikshaRoute = signal<boolean>(false)
    /** Sales cycle picker: home dashboard only (RM/ZM), not revenue or RUC routes. */
    readonly showSalesCycle = computed(() => {
        const loggedInUserRole = this.loggedInUserRole()
        return (
            this.isHomeRoute() &&
            (loggedInUserRole === EUserType.RM || loggedInUserRole === EUserType.ZM)
        )
    })

    private readonly notificationsViewedKey = 'notifications_viewed'
    private routerSubscription?: Subscription
    private readonly destroyRef = inject(DestroyRef)
    private readonly dashboardService = inject(IDashboardService)
    private readonly shikshaDashboardFilters = inject(ShikshaDashboardFiltersService)
    private readonly reporteesService = inject(ReporteesService)
    readonly shikshaScopeLabel = SHIKSHA_DROPDOWN_CONTENT.scopeLabel
    readonly shikshaYearLabel = SHIKSHA_DROPDOWN_CONTENT.yearLabel
    readonly shikshaRefreshAriaLabel = SHIKSHA_DROPDOWN_CONTENT.refreshAriaLabel
    readonly shikshaScopeControl = new FormControl<string>(
        this.shikshaDashboardFilters.selectedScope(),
        { nonNullable: true },
    )
    readonly shikshaYearControl = new FormControl<string>(
        this.shikshaDashboardFilters.selectedYear(),
        { nonNullable: true },
    )
    readonly shikshaScopeDropdownConfig = this.shikshaDashboardFilters.scopeDropdownConfig
    readonly shikshaYearDropdownConfig = this.shikshaDashboardFilters.yearDropdownConfig
    readonly isShikshaRefreshing = this.shikshaDashboardFilters.isRefreshing
    readonly lastShikshaRefreshedAt = this.shikshaDashboardFilters.lastRefreshedAt
    private readonly refreshTimeNow = signal<number>(Date.now())
    readonly shikshaRefreshLabel = computed(() => {
        this.refreshTimeNow()
        const lastRefreshedAt = this.lastShikshaRefreshedAt()
        if (!lastRefreshedAt) {
            return 'Not refreshed yet'
        }

        const elapsedRefreshMs = Date.now() - lastRefreshedAt.getTime()
        if (elapsedRefreshMs < AppConstants.REFRESH_LABEL_UPDATE_INTERVAL_MS) {
            return 'Updated just now'
        }

        const elapsedRefreshMinutes = Math.floor(
            elapsedRefreshMs / AppConstants.REFRESH_LABEL_UPDATE_INTERVAL_MS,
        )
        if (elapsedRefreshMinutes < AppConstants.SHIKSHA_REFRESH_LABEL_MAX_RELATIVE_MINUTES) {
            return `Updated ${elapsedRefreshMinutes}m ago`
        }

        return `Updated ${lastRefreshedAt.toLocaleTimeString('en-IN', {
            hour: 'numeric',
            minute: '2-digit',
        })}`
    })
    constructor() {
        this.initializeNotificationState()
        this.subscribeToUserData()
        this.shikshaScopeControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                if (value) {
                    this.shikshaDashboardFilters.setSelectedScope(value)
                }
            })
        this.shikshaYearControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                if (value) {
                    this.shikshaDashboardFilters.setSelectedYear(value)
                }
            })
        effect(() => {
            const selectedScope = this.shikshaDashboardFilters.selectedScope()
            if (this.shikshaScopeControl.value !== selectedScope) {
                this.shikshaScopeControl.setValue(selectedScope, { emitEvent: false })
            }
        })
        effect(() => {
            const selectedYear = this.shikshaDashboardFilters.selectedYear()
            if (this.shikshaYearControl.value !== selectedYear) {
                this.shikshaYearControl.setValue(selectedYear, { emitEvent: false })
            }
        })

        combineLatest([toObservable(this.isShikshaRoute), toObservable(this.userStore.userData)])
            .pipe(
                switchMap(([onShikshaRoute, user]) => {
                    if (!onShikshaRoute || !user?.user_id) {
                        return of(null)
                    }
                    return this.reporteesService.getReportees(user.user_id).pipe(
                        map((response) => {
                            const reportees =
                                response.success && response.data?.reportees?.length
                                    ? response.data.reportees
                                    : []
                            return {
                                hasTeam: reportees.length > 0,
                                reportees,
                            }
                        }),
                        catchError(() => of({ hasTeam: false, reportees: [] })),
                    )
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((teamScopePayload) => {
                if (teamScopePayload === null) {
                    this.shikshaDashboardFilters.setTeamScopeAvailable(true)
                    this.shikshaDashboardFilters.setReporteesForScope([])
                    return
                }
                this.shikshaDashboardFilters.setTeamScopeAvailable(teamScopePayload.hasTeam)
                this.shikshaDashboardFilters.setReporteesForScope(teamScopePayload.reportees)
            })
    }

    ngOnInit(): void {
        const refreshIntervalId = window.setInterval(() => {
            this.refreshTimeNow.set(Date.now())
        }, AppConstants.REFRESH_LABEL_UPDATE_INTERVAL_MS)

        this.routerSubscription = this.router.events
            .pipe(
                filter(
                    (routerEvent): routerEvent is NavigationEnd =>
                        routerEvent instanceof NavigationEnd,
                ),
                startWith(new NavigationEnd(0, this.router.url, this.router.url)),
                map((navigationEndEvent) =>
                    this.normalizePath(navigationEndEvent.urlAfterRedirects),
                ),
            )
            .subscribe((path) => {
                this.isHomeRoute.set(path === '/')
                this.isShikshaRoute.set(path === '/shiksha')
            })
    }

    ngOnDestroy(): void {
        this.routerSubscription?.unsubscribe()
    }

    private subscribeToUserData(): void {
        effect(() => {
            const userData = this.userStore.userData()
            this.userInfo = userData
            this.isAuthenticated.set(!!userData)
            if (userData) {
                this.processBitwisePermissions()
                    .then(() => this.setUserRoleFromPermissions())
                    .catch(() => {
                        this.loggedInUserRole.set(null)
                    })
            } else {
                this.loggedInUserRole.set(null)
            }
        })
    }

    private initializeNotificationState(): void {
        const notificationsViewed = localStorage.getItem(this.notificationsViewedKey)
        if (notificationsViewed === 'true') {
            this.notificationCount.set(0)
        }
    }

    onNotificationsMenuOpened(): void {
        this.markAllAsRead()
        localStorage.setItem(this.notificationsViewedKey, 'true')
    }

    markAllAsRead(): void {
        this.notificationCount.set(0)
    }

    getNotificationCount = (): string =>
        this.notificationCount() ? this.notificationCount().toString() : ''

    signIn(): void {
        const nextUrl = window.location.href
        window.location.href = `${environment.ssoBaseUrl}${authEndpoints.SIGNIN_PATH}?next=${nextUrl}`
    }

    private async processBitwisePermissions(): Promise<void> {
        const bitwisePermissions = await this.permissionService.getPermissionsBitwise()
        this.userStore.setPermissions(bitwisePermissions as unknown as UserPermissions)
    }

    private async setUserRoleFromPermissions(): Promise<void> {
        const resolvedUserRole = await determineUserRoleFromPermissions(this.permissionService)
        this.loggedInUserRole.set(resolvedUserRole)
    }

    userAvatarUrl = computed(() => this.userInfo?.avatar_url ?? this.avatarPath)

    userDisplayName = computed(
        () =>
            [this.userInfo?.name?.first, this.userInfo?.name?.last].filter(Boolean).join(' ') || '',
    )

    readonly salesCycleOptions = this.dashboardService.salesCycleOptions
    readonly selectedSalesCycle = this.dashboardService.selectedSalesCycle
    readonly selectedSalesCycleStartYear = this.dashboardService.selectedAcademicYearStart

    selectSalesCycle(startYear: number): void {
        this.dashboardService.setSelectedAcademicYearStart(startYear)
    }

    refreshShikshaDashboard(): void {
        this.shikshaDashboardFilters.requestRefresh()
    }

    private normalizePath(url: string): string {
        const [path] = url.split('?')
        return path || ''
    }
}
